import { useCallback, useEffect, useMemo, useRef, useState, JSX } from 'react';
import { MmmFeatureNameResponse } from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';
import './EfficientFrontier.scss';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { MdPushPin } from 'react-icons/md';
import { ChartContainer } from '@Components/ChartContainer';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Intent, Placement, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { isRawFeatureId } from '@src/utils/mmm-feature-mapping';
import { PinnedSpendPointsTable } from './Components/PinnedSpendTable';
import { MarketingOutcomes } from './Components/MarketingOutcomes';
import { PinnedRightActions } from './Components/PinnedRightActions';
import { ModeledRightActions } from './Components/MarketingOutcomesRightActions';
import { ModelConfig, PinnedSpendPointData, ChannelMixDataPoint } from '../hroas-types';
import { ChannelMix } from './Components/ChannelMix';
import { ChannelMixModal } from './Components/ChannelMixModal';
import {
  applySelectedModelsFilter,
  buildEfficientFrontierSeries,
  buildMarketingOutcomePoints,
  computeBauWeeklySpend,
  computeInterpolatedBau,
  getChannelGroups,
  getChannelMixData,
  getDateRangeLengthDays,
  sumActualSpendInDateRange
} from './utils/efficientFrontierUtils';
import { resolveRawFeatureName } from '../utils/featureIdResolution';
import { FeatureMapsByModelType } from '../utils/useFeatureMapping';
import { Slot } from '../utils/slots';
import { transformSpendEffectShareCsvToTransformedData } from '../utils/hroasUtils';
import { useSpendEffectShareCsvData } from '../utils/useModelResultsData';
import {
  useChannelMixData,
  useMarketingOutcomesData,
  useMarketingOutcomesDateRange
} from '../utils/useEfficientFrontierData';
import { useScenarioPlanningToolBar } from '../../GeoMMM/ScenarioPlanning/UseScenarioPlanningToolBar';
import {
  AdjustModal,
  AdjustModalProps,
  PLAN_OPTIONS
} from '../../GeoMMM/ScenarioPlanning/Modals/AdjustModal';
import {
  SetPlanLengthModal,
  SetPlanLengtModalProps
} from '../../GeoMMM/ScenarioPlanning/Modals/SetPlanLengthModal';
import './Components/AdjustTimeFrameModal.scss';

const EF_ADJUST_OPTIONS: { text: PLAN_OPTIONS; subtext: string }[] = [
  {
    text: PLAN_OPTIONS.KEEP_SPEND_AMOUNTS,
    subtext: 'Preserve your current spend values. Percent changes will adjust automatically.'
  },
  {
    text: PLAN_OPTIONS.KEEP_PERCENT_CHANGES,
    subtext: 'Reapply your % change values to the new baseline data.'
  },
  {
    text: PLAN_OPTIONS.START_FRESH,
    subtext: 'Remove all pins and start fresh with the new baseline data.'
  }
];

const EF_OPTION_LABELS: Partial<Record<PLAN_OPTIONS, string>> = {
  [PLAN_OPTIONS.KEEP_SPEND_AMOUNTS]: 'Keep pinned spend amounts',
  [PLAN_OPTIONS.KEEP_PERCENT_CHANGES]: 'Keep pinned percent changes',
  [PLAN_OPTIONS.START_FRESH]: 'Start fresh'
};

const EFAdjustModal = (props: AdjustModalProps): JSX.Element => (
  <AdjustModal
    {...props}
    title='Adjust Time Frame'
    className='ef-adjust-time-frame-modal'
    infoText='How should we handle your existing pinned spend points?'
    options={EF_ADJUST_OPTIONS}
    optionLabels={EF_OPTION_LABELS}
  />
);

const EFSetTimeFrameModal = (props: SetPlanLengtModalProps): JSX.Element => (
  <SetPlanLengthModal
    {...props}
    headerText='Time Frame'
    infoText='Reference dates will automatically update to match your time frame.'
  />
);

interface PendingAdjust {
  option: PLAN_OPTIONS;
  oldPins: Map<number, PinnedSpendPointData>;
  oldBauBudget: number;
  oldBudgetKeyMapping: Map<number, number>;
}

interface EfficientFrontierProps {
  models: ModelConfig[];
  slots: Slot[];
  kpi: string;
  runId: string | null;
  // Per-model-type feature_id -> pretty-name maps (keyed "rMMM"/"GeoMMM"/"BrandEquity") plus a
  // single representative map, warmed at the HROAS page level. Used to resolve raw feature-id
  // channel keys in the Channel Mix chart to human display names — the same maps Model Results
  // resolves against. Undefined for old-format clients / while a map is still loading.
  featureMaps?: FeatureMapsByModelType;
  featureMap?: MmmFeatureNameResponse;
  // Mounted while hidden; only the active tab may pin its toolbar into the Page chrome.
  active: boolean;
}

export const EfficientFrontier = ({
  models,
  slots,
  kpi,
  runId,
  featureMaps,
  featureMap,
  active
}: EfficientFrontierProps): JSX.Element => {
  const { cid: originalCompany } = useCompanyInfo();
  const setError = useSetError();

  const dateRangeQuery = useMarketingOutcomesDateRange(originalCompany, kpi, runId);
  const maxEnd = dateRangeQuery.data?.maxEnd;
  const minStart = dateRangeQuery.data?.minStart;

  const adjustScenarioRef = useRef<((option: PLAN_OPTIONS) => void) | null>(null);

  const { start, end, planLength, planLengthLabel, toolbar } = useScenarioPlanningToolBar({
    adjustScenario: (option: PLAN_OPTIONS) => adjustScenarioRef.current?.(option),
    AdjustModalComponent: EFAdjustModal,
    maxEnd,
    minStart,
    PlanLengthModalComponent: EFSetTimeFrameModal,
    pinToPageChrome: active,
    showEfficientFrontierControls: true,
    showScenarioPlanningControls: false
  });

  const marketingOutcomesQuery = useMarketingOutcomesData(originalCompany, kpi, runId, start, end);
  const channelMixQuery = useChannelMixData(originalCompany, kpi, runId, planLength);
  // DEV-21410: actual spend over the selected date range, used to compute BAU dynamically instead
  // of trusting the routine's fixed isBau anchor. Keyed identically to Model Results' SES query
  // (company/kpi/'model_results'/runId), so this dedupes against any other panel reading it.
  const spendEffectShareQuery = useSpendEffectShareCsvData(
    originalCompany,
    kpi,
    runId,
    featureMaps
  );

  const rawMarketingOutcomesData = marketingOutcomesQuery.data?.processedData;
  const rawChannelMixData = channelMixQuery.data;
  const budgetKeyMapping = marketingOutcomesQuery.data?.budgetKeyMapping;
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [modeledDropdownValue, setModeledDropdownValue] = useState<
    'Marketing Outcomes' | 'Channel Mix'
  >('Marketing Outcomes');
  const [showMarketingOutcomesGraphView, setShowMarketingOutcomesGraphView] =
    useState<boolean>(true);
  const [showPinnedTableView, setShowPinnedTableView] = useState<boolean>(true);
  const [pinnedSpendPoints, setPinnedSpendPoints] = useState<Map<number, PinnedSpendPointData>>(
    new Map()
  );
  const [pendingAdjust, setPendingAdjust] = useState<PendingAdjust | null>(null);
  const isPinsInitialized = useRef(false);
  const [viewType, setViewType] = useState<'absolute' | 'relative'>('absolute');

  const enabledModels = useMemo(
    () => new Set(models.filter(model => model.enabled).map(model => model.id)),
    [models]
  );
  const stackSeries = useMemo(
    () => buildEfficientFrontierSeries(slots, models, 'stack'),
    [slots, models]
  );
  const displaySeries = useMemo(
    () => buildEfficientFrontierSeries(slots, models, 'display'),
    [slots, models]
  );

  useEffect(() => {
    setSelectedModels(prev => {
      const newSet = new Set(prev);
      // Remove any models that are no longer enabled
      prev.forEach(model => {
        if (!enabledModels.has(model)) {
          newSet.delete(model);
        }
      });
      // Add any newly enabled models
      enabledModels.forEach(model => {
        if (!prev.has(model)) {
          newSet.add(model);
        }
      });
      return newSet;
    });
  }, [enabledModels]);

  useEffect(() => {
    if (marketingOutcomesQuery.isError && marketingOutcomesQuery.error) {
      setError({
        message: 'Error fetching marketing outcomes data',
        reportError: marketingOutcomesQuery.error
      });
    }
  }, [marketingOutcomesQuery.isError, marketingOutcomesQuery.error, setError]);

  useEffect(() => {
    if (channelMixQuery.isError && channelMixQuery.error) {
      setError({
        message: 'Error fetching channel mix data',
        reportError: channelMixQuery.error
      });
    }
  }, [channelMixQuery.isError, channelMixQuery.error, setError]);

  useEffect(() => {
    if (spendEffectShareQuery.isError && spendEffectShareQuery.error) {
      setError({
        message: 'Error fetching spend data for business-as-usual calculation',
        reportError: spendEffectShareQuery.error
      });
    }
  }, [spendEffectShareQuery.isError, spendEffectShareQuery.error, setError]);

  const marketingOutcomesData = useMemo(
    () =>
      rawMarketingOutcomesData ? buildMarketingOutcomePoints(rawMarketingOutcomesData, slots) : [],
    [rawMarketingOutcomesData, slots]
  );

  // Resolve raw feature-id channel keys (new-format runs surface e.g. "featid1225") to their human
  // display names, mirroring how Model Results resolves feature ids via the per-model-type maps.
  // Channel-mix rows don't carry a per-row model_type, so try every warmed map (plus the single
  // representative one) and take the first hit. Old-format channels (human names / "__"-delimited)
  // aren't raw feature ids, so resolveRawFeatureName passes them through untouched — no change for
  // poppi / tinuititest.
  const resolveChannelName = useCallback(
    (raw: string): string => {
      if (!isRawFeatureId(raw)) {
        return raw;
      }
      const candidateMaps = [...Object.values(featureMaps ?? {}), featureMap].filter(
        (map): map is MmmFeatureNameResponse => Boolean(map)
      );
      for (const map of candidateMaps) {
        const resolved = resolveRawFeatureName(raw, map);
        if (resolved !== raw) {
          return resolved;
        }
      }
      return raw;
    },
    [featureMaps, featureMap]
  );

  // Rewrite each channel key to its resolved display name BEFORE grouping/color assignment, so the
  // grouped channel names (the data keys, color-map keys, and visible labels) are all the human
  // names in lockstep — and feature ids that share a channel merge into one bar, matching the
  // original bpm-frontend where channel keys were already human-readable.
  const resolvedChannelMixData = useMemo(() => {
    if (!rawChannelMixData) {
      return rawChannelMixData;
    }
    return rawChannelMixData.map(budgetData => ({
      ...budgetData,
      channels: budgetData.channels.map(channel => ({
        ...channel,
        channel: resolveChannelName(channel.channel)
      }))
    }));
  }, [rawChannelMixData, resolveChannelName]);

  const channelMixChartData = useMemo(() => {
    if (!resolvedChannelMixData || !budgetKeyMapping) {
      return [];
    }
    // Map the raw channel mix data to use aggregated budget values that match marketing outcomes
    // Filter out any data points that don't have a valid mapping to avoid mixing scales
    const mappedChannelMixData = resolvedChannelMixData
      .filter(budgetData => budgetKeyMapping.has(budgetData.totalBudget))
      .map(budgetData => ({
        ...budgetData,
        totalBudget: budgetKeyMapping.get(budgetData.totalBudget)!
      }));
    const channelMixData = getChannelMixData(mappedChannelMixData, false);
    // Sort by totalBudget to ensure area chart renders correctly
    return channelMixData.sort((a, b) => a.totalBudget - b.totalBudget);
  }, [resolvedChannelMixData, budgetKeyMapping]);

  const channelGroups = useMemo(() => {
    if (!resolvedChannelMixData) {
      return [];
    }
    return getChannelGroups(resolvedChannelMixData);
  }, [resolvedChannelMixData]);

  const [selectedChannelMixPoint, setSelectedChannelMixPoint] =
    useState<ChannelMixDataPoint | null>(null);
  const [showChannelMixModal, setShowChannelMixModal] = useState<boolean>(false);

  // DEV-21410: BAU is computed dynamically from the user's selected date range instead of
  // trusting the routine's fixed isBau row. `enabledModelIds` mirrors ModelOverview's csvSpendData
  // derivation (models.enabled, not the page's display-only selectedModels toggle) -- actual spend
  // is a physical quantity independent of which model curves the analyst is currently viewing.
  const enabledModelIds = useMemo(
    () => models.filter(model => model.enabled).map(model => model.id),
    [models]
  );

  const csvSpendData = useMemo(
    () =>
      spendEffectShareQuery.data
        ? transformSpendEffectShareCsvToTransformedData(spendEffectShareQuery.data, enabledModelIds)
        : [],
    [spendEffectShareQuery.data, enabledModelIds]
  );

  // Sum of actual spend across all channels/weeks in the selected range -- the bracket target,
  // on the same (period-total) basis as marketingOutcomesData's/channelMixChartData's totalBudget
  // values (see the efficientFrontierUtils.ts note). Reported alongside on the true weekly basis
  // (divided by planLength) since that's the natural unit for comparing against the routine's own
  // per-week budget-level scenarios.
  const bauTargetBudget = useMemo(
    () => sumActualSpendInDateRange(csvSpendData, start, end),
    [csvSpendData, start, end]
  );
  const bauWeeklySpend = useMemo(
    () => computeBauWeeklySpend(csvSpendData, start, end, planLength),
    [csvSpendData, start, end, planLength]
  );

  const channelMixByBudget = useMemo(() => {
    const map = new Map<number, ChannelMixDataPoint>();
    channelMixChartData.forEach(point => map.set(point.totalBudget, point));
    return map;
  }, [channelMixChartData]);

  const bauInterpolation = useMemo(() => {
    if (marketingOutcomesData.length === 0 || bauTargetBudget <= 0) {
      return undefined;
    }
    return computeInterpolatedBau(marketingOutcomesData, channelMixByBudget, bauTargetBudget);
  }, [marketingOutcomesData, channelMixByBudget, bauTargetBudget]);

  // DEV-21410: when the selected date range's implied BAU spend falls below the lowest modeled
  // budget level, BAU is suppressed entirely (no chart point, no pinned-table row) rather than
  // clamped-and-displayed -- see `businessAsUsualFiltered` below. The "above highest" direction is
  // untouched: it still clamps and displays, via `bauOutOfRangeMessage`.
  const isBauBelowLowestModeledBudget = bauInterpolation?.clampedDirection === 'min';

  const businessAsUsual: PinnedSpendPointData | undefined = bauInterpolation?.point;

  // The one canonical, toggle-aware BAU value -- every place that displays or stores BAU's
  // revenue/ROAS (the synthetic chart row, the pinned-points map, the pinned table) derives from
  // this instead of independently recomputing its own filtered copy of `businessAsUsual`, so they
  // can never disagree with each other or with the visible curve height at BAU's budget. Resolves
  // to `undefined` (same as the "no BAU data at all" case) when DEV-21410's below-lowest suppression
  // applies, so every downstream consumer's existing "no BAU" fallback handles this for free.
  const businessAsUsualFiltered = useMemo(
    () =>
      businessAsUsual && !isBauBelowLowestModeledBudget
        ? applySelectedModelsFilter(businessAsUsual, selectedModels)
        : undefined,
    [businessAsUsual, isBauBelowLowestModeledBudget, selectedModels]
  );

  // Filter based on selected models and calculate holistic values from enabled models only.
  const filteredMarketingOutcomesData = useMemo(() => {
    if (marketingOutcomesData.length > 0 && selectedModels.size > 0) {
      return marketingOutcomesData.map(dataPoint =>
        applySelectedModelsFilter(dataPoint, selectedModels)
      );
    }
    return [];
  }, [marketingOutcomesData, selectedModels]);

  // BAU's budget is linearly interpolated between two real modeled levels, so it essentially never
  // coincides with a real chartData x-value -- recharts' Tooltip only does nearest-index lookup
  // against `data`, with no awareness of ReferenceLine. Injecting BAU as a real (flagged) row lets
  // that native lookup resolve to BAU's own values on hover instead of a neighboring real point's.
  // Injected AFTER filtering (rather than before, as the prior commit did) so the synthetic row's
  // values are always exactly `businessAsUsualFiltered`'s -- never run through a second, redundant
  // pass of the per-model filter.
  const filteredMarketingOutcomesDataWithBau = useMemo(() => {
    if (!businessAsUsualFiltered) {
      return filteredMarketingOutcomesData;
    }
    if (
      filteredMarketingOutcomesData.some(d => d.totalBudget === businessAsUsualFiltered.totalBudget)
    ) {
      return filteredMarketingOutcomesData;
    }
    const syntheticBauPoint: PinnedSpendPointData = {
      ...businessAsUsualFiltered,
      isSyntheticBau: true
    };
    return [...filteredMarketingOutcomesData, syntheticBauPoint].sort(
      (a, b) => a.totalBudget - b.totalBudget
    );
  }, [filteredMarketingOutcomesData, businessAsUsualFiltered]);

  // Channel-mix budgets are per-channel spend figures with no notion of "selected models" -- there
  // is no analogous filter to apply here, so the synthetic point is built straight from the
  // (budget-only) interpolation result.
  const channelMixChartDataWithBau = useMemo(() => {
    if (!businessAsUsualFiltered || !bauInterpolation) {
      return channelMixChartData;
    }
    if (
      channelMixChartData.some(point => point.totalBudget === businessAsUsualFiltered.totalBudget)
    ) {
      return channelMixChartData;
    }
    const syntheticBauPoint: ChannelMixDataPoint = {
      totalBudget: businessAsUsualFiltered.totalBudget,
      ...bauInterpolation.channelBudgets
    };
    return [...channelMixChartData, syntheticBauPoint].sort(
      (a, b) => a.totalBudget - b.totalBudget
    );
  }, [channelMixChartData, businessAsUsualFiltered, bauInterpolation]);

  useEffect(() => {
    if (!bauInterpolation || !import.meta.env.DEV) {
      return;
    }
    if (!bauInterpolation.isBudgetConsistent) {
      console.warn(
        '[EfficientFrontier] DEV-21410 BAU basis-consistency check failed: sum of interpolated ' +
          'channel budgets does not match the interpolated total budget within tolerance.',
        {
          totalBudgetBau: bauInterpolation.totalBudgetBau,
          totalBudget: bauInterpolation.point.totalBudget,
          bauWeeklySpend
        }
      );
    }
  }, [bauInterpolation, bauWeeklySpend]);

  // Surfaced as a lightweight inline note (not a modal -- the page still functions correctly,
  // BAU is simply clamped to the nearest modeled endpoint rather than extrapolated) next to the
  // chart title. See PinnedRightActions/InfoTooltip usage elsewhere on this page for the pattern.
  // DEV-21410: only the "above highest" direction still clamps-and-displays this way -- "below
  // lowest" now suppresses BAU entirely and surfaces a one-shot popup instead (see below), so an
  // inline note pointing at a chart point that no longer exists would be confusing.
  const bauOutOfRangeMessage =
    bauInterpolation?.isOutOfRange && bauInterpolation.clampedDirection === 'max'
      ? 'The business-as-usual spend for the selected date range falls above the highest modeled ' +
        'budget level. Showing the nearest modeled estimate instead of extrapolating.'
      : null;

  // DEV-21410: pop up a one-time warning per date-range change that produces a below-lowest BAU,
  // rather than firing on every render while the condition holds (or re-firing while the user stays
  // on the same out-of-range range). Tracks the (start, end) pair the popup was last shown for, so
  // it fires again only when the range actually changes to a (still or newly) out-of-range value --
  // mirrors the "compare against the previous range" shape of `prevRangeRef` above, but scoped to
  // this one concern instead of the pin-reconciliation logic that ref feeds.
  const lastWarnedRangeRef = useRef<string | null>(null);
  useEffect(() => {
    const rangeKey = `${start}|${end}`;
    if (!isBauBelowLowestModeledBudget) {
      lastWarnedRangeRef.current = null;
      return;
    }
    if (lastWarnedRangeRef.current === rangeKey) {
      return;
    }
    lastWarnedRangeRef.current = rangeKey;
    setError({
      title: 'Business-as-usual not displayed',
      intent: Intent.WARNING,
      message:
        'The date range selected has lower spend than the lowest pre-calculated budget ' +
        'optimization for the Efficient Frontier so BAU is not displayed. Please select a date ' +
        'range with a higher spend level to display BAU levels on the graph.'
    });
  }, [isBauBelowLowestModeledBudget, start, end, setError]);

  // eslint-disable-next-line react-hooks/refs -- intentionally storing the latest closure in a ref that is only invoked later from the toolbar's adjustScenario callback, never read during render
  adjustScenarioRef.current = (option: PLAN_OPTIONS) => {
    if (!businessAsUsualFiltered || !budgetKeyMapping) {
      return;
    }
    setPendingAdjust({
      option,
      oldPins: new Map(pinnedSpendPoints),
      oldBauBudget: businessAsUsualFiltered.totalBudget,
      oldBudgetKeyMapping: new Map(budgetKeyMapping)
    });
  };

  useEffect(() => {
    if (businessAsUsualFiltered && !isPinsInitialized.current) {
      setPinnedSpendPoints(
        new Map([
          [businessAsUsualFiltered.totalBudget, { ...businessAsUsualFiltered, isAutoBau: true }]
        ])
      );
      isPinsInitialized.current = true;
    }
  }, [businessAsUsualFiltered]);

  // DEV-24477: classify a Reference Range change by whether the new range preserves the previous
  // range's length in days, so the reconciliation effect below knows whether to keep-and-revalidate
  // pins (same length) or clear them outright (different length). Tracked separately from that
  // effect (which only re-runs once `marketingOutcomesData` has actually refetched for the new
  // range) so the comparison never runs against stale, pre-refetch data.
  const prevRangeRef = useRef<{ start: string; end: string } | null>(null);
  const rangeChangeKindRef = useRef<'same-length' | 'different-length' | null>(null);

  useEffect(() => {
    const prevRange = prevRangeRef.current;
    // Always sync the ref first so it can't be skipped by an early return below.
    prevRangeRef.current = { start, end };

    const isFirstRun = !prevRange;
    const isSameValue = prevRange && prevRange.start === start && prevRange.end === end;
    if (isFirstRun || isSameValue) {
      return;
    }

    rangeChangeKindRef.current =
      getDateRangeLengthDays(prevRange.start, prevRange.end) === getDateRangeLengthDays(start, end)
        ? 'same-length'
        : 'different-length';
  }, [start, end]);

  useEffect(() => {
    if (!marketingOutcomesData.length || pendingAdjust) {
      return;
    }
    const rangeChangeKind = rangeChangeKindRef.current;
    rangeChangeKindRef.current = null;
    setPinnedSpendPoints(prev => {
      const updatedMap = new Map<number, PinnedSpendPointData>();
      // DEV-24477: a Reference Range change to a *different*-length range clears every user pin --
      // a pinned budget from the old range has no meaningful relationship to the new one and would
      // otherwise dangle with no matching chart x-value (no hover behavior). Leaving updatedMap
      // empty here (skipping the loop below entirely) is that clear.
      if (rangeChangeKind !== 'different-length') {
        prev.forEach((pinnedPoint, budget) => {
          // Drop the previous auto-inserted BAU entry -- identified by its own flag rather than by
          // matching against a remembered budget key -- so it's never mistaken for (or laundered
          // into) a real user pin. The fresh BAU entry is re-added below.
          if (pinnedPoint.isAutoBau) {
            return;
          }
          const currentData = marketingOutcomesData.find(d => d.totalBudget === budget);
          if (currentData) {
            updatedMap.set(budget, currentData);
          } else if (rangeChangeKind !== 'same-length') {
            // Not a range-length-preserving change (e.g. a company/kpi/runId switch) -- preserve
            // the pre-DEV-24477 fallback of keeping the stale pin rather than dropping it.
            updatedMap.set(budget, pinnedPoint);
          }
          // rangeChangeKind === 'same-length' with no match: intentionally dropped -- the pinned
          // budget no longer resolves to a real modeled point in the (same-length) new range.
        });
      }
      if (businessAsUsualFiltered && !updatedMap.has(businessAsUsualFiltered.totalBudget)) {
        updatedMap.set(businessAsUsualFiltered.totalBudget, {
          ...businessAsUsualFiltered,
          isAutoBau: true
        });
      }
      return updatedMap;
    });
  }, [marketingOutcomesData, pendingAdjust, businessAsUsualFiltered]);

  useEffect(() => {
    if (!pendingAdjust || !marketingOutcomesData.length || !businessAsUsualFiltered) {
      return;
    }

    const { option, oldPins, oldBauBudget, oldBudgetKeyMapping } = pendingAdjust;
    const newBudgetKeyMapping = budgetKeyMapping ?? new Map<number, number>();

    const oldPeriodToWeekly = new Map<number, number>();
    oldBudgetKeyMapping.forEach((periodTotal, weekly) =>
      oldPeriodToWeekly.set(periodTotal, weekly)
    );

    const snapToNearest = (target: number): PinnedSpendPointData =>
      marketingOutcomesData.reduce((best, d) =>
        Math.abs(d.totalBudget - target) < Math.abs(best.totalBudget - target) ? d : best
      );

    let newPins: Map<number, PinnedSpendPointData>;

    if (option === PLAN_OPTIONS.START_FRESH) {
      newPins = new Map([
        [businessAsUsualFiltered.totalBudget, { ...businessAsUsualFiltered, isAutoBau: true }]
      ]);
    } else if (option === PLAN_OPTIONS.KEEP_SPEND_AMOUNTS) {
      newPins = new Map();
      oldPins.forEach((oldPoint, oldPeriodTotal) => {
        // Skip the previous auto-BAU entry -- it isn't a real user pin, so carrying it through this
        // remapping would "launder" it into an indistinguishable phantom pin at the transformed
        // budget. The real BAU replacement is inserted fresh below.
        if (oldPoint.isAutoBau) {
          return;
        }
        const weeklyBudget = oldPeriodToWeekly.get(oldPeriodTotal);
        const newPeriodTotal =
          weeklyBudget !== undefined ? newBudgetKeyMapping.get(weeklyBudget) : undefined;
        const newPoint =
          newPeriodTotal !== undefined
            ? (marketingOutcomesData.find(d => d.totalBudget === newPeriodTotal) ??
              snapToNearest(newPeriodTotal))
            : snapToNearest(oldPeriodTotal);
        newPins.set(newPoint.totalBudget, newPoint);
      });
    } else {
      const oldBauWeekly = oldPeriodToWeekly.get(oldBauBudget);
      const newBauPeriodTotal =
        oldBauWeekly !== undefined
          ? (newBudgetKeyMapping.get(oldBauWeekly) ?? businessAsUsualFiltered.totalBudget)
          : businessAsUsualFiltered.totalBudget;

      newPins = new Map();
      oldPins.forEach((oldPoint, oldPeriodTotal) => {
        // Same exclusion as the KEEP_SPEND_AMOUNTS branch above.
        if (oldPoint.isAutoBau) {
          return;
        }
        const pctChange = oldBauBudget > 0 ? (oldPeriodTotal - oldBauBudget) / oldBauBudget : 0;
        const targetBudget = newBauPeriodTotal * (1 + pctChange);
        const newPoint = snapToNearest(targetBudget);
        newPins.set(newPoint.totalBudget, newPoint);
      });
    }

    if (!newPins.has(businessAsUsualFiltered.totalBudget)) {
      newPins.set(businessAsUsualFiltered.totalBudget, {
        ...businessAsUsualFiltered,
        isAutoBau: true
      });
    }

    setPinnedSpendPoints(newPins);
    setPendingAdjust(null);
  }, [marketingOutcomesData, pendingAdjust, businessAsUsualFiltered, budgetKeyMapping]);

  return (
    <>
      {toolbar}
      {filteredMarketingOutcomesDataWithBau &&
      channelMixChartData &&
      channelGroups &&
      // DEV-21410: the page shell must still render (with BAU hidden) once data has genuinely
      // loaded and BAU was deliberately suppressed for the below-lowest-budget case -- only fall
      // back to the loading spinner while BAU data hasn't resolved at all yet.
      (businessAsUsualFiltered || isBauBelowLowestModeledBudget) ? (
        <div className='ef-page-body'>
          <ChartContainer
            className='ef-marketing-outcomes-card'
            title={
              <div className='ef-modeled-container-title'>
                <span>Modeled Investment vs</span>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  type={DropdownToggleType.WIDGET_TITLE}
                  className='chart-title-dropdown'
                  options={[
                    { label: 'Media-Driven Outcomes', value: 'Marketing Outcomes' },
                    'Channel Mix'
                  ]}
                  onChange={value => {
                    if (value === 'Marketing Outcomes') {
                      setModeledDropdownValue('Marketing Outcomes');
                    }
                    if (value === 'Channel Mix') {
                      setModeledDropdownValue('Channel Mix');
                    }
                  }}
                  value={modeledDropdownValue}
                />
                <span className='ef-plan-range-title'>{planLengthLabel}</span>
                {bauOutOfRangeMessage && <InfoTooltip inline>{bauOutOfRangeMessage}</InfoTooltip>}
              </div>
            }
            rightActions={
              <ModeledRightActions
                viewType={viewType}
                setViewType={setViewType}
                modeledDropdownValue={modeledDropdownValue}
                showModeledGraphView={showMarketingOutcomesGraphView}
                setShowModeledGraphView={setShowMarketingOutcomesGraphView}
              />
            }
          >
            {modeledDropdownValue === 'Marketing Outcomes' && (
              <MarketingOutcomes
                chartData={filteredMarketingOutcomesDataWithBau}
                fullChartData={marketingOutcomesData}
                businessAsUsual={businessAsUsualFiltered ?? null}
                pinnedSpendPoints={pinnedSpendPoints}
                setPinnedSpendPoints={setPinnedSpendPoints}
                stackSeries={stackSeries}
                displaySeries={displaySeries}
                selectedModels={selectedModels}
                setSelectedModels={setSelectedModels}
              />
            )}
            {modeledDropdownValue === 'Channel Mix' && (
              <ChannelMix
                businessAsUsual={businessAsUsualFiltered}
                chartData={channelMixChartDataWithBau}
                channelGroups={channelGroups}
                viewType={viewType}
                pinnedSpendPoints={pinnedSpendPoints}
                setPinnedSpendPoints={setPinnedSpendPoints}
                marketingOutcomesChartData={marketingOutcomesData}
              />
            )}
          </ChartContainer>
          <ChartContainer
            title={
              <div className='ef-pinned-container-title'>
                <span>Pinned Spend Points</span>
                <MdPushPin />
              </div>
            }
            rightActions={
              <PinnedRightActions
                showPinnedTableView={showPinnedTableView}
                setShowPinnedTableView={setShowPinnedTableView}
              />
            }
          >
            <PinnedSpendPointsTable
              businessAsUsual={businessAsUsualFiltered}
              setPinnedSpendPoints={setPinnedSpendPoints}
              pinnedSpendPointsData={Array.from(pinnedSpendPoints.values()).sort(
                (a, b) => b.totalBudget - a.totalBudget
              )}
              series={displaySeries}
              onPieChartClick={pinnedPoint => {
                const channelMixPoint = channelMixChartData.find(
                  point => point.totalBudget === pinnedPoint.totalBudget
                );
                if (channelMixPoint) {
                  setSelectedChannelMixPoint(channelMixPoint);
                  setShowChannelMixModal(true);
                }
              }}
            />
          </ChartContainer>
          <ChannelMixModal
            show={showChannelMixModal}
            onHide={() => {
              setShowChannelMixModal(false);
              setSelectedChannelMixPoint(null);
            }}
            dataPoint={selectedChannelMixPoint}
            channelGroups={channelGroups}
          />
        </div>
      ) : (
        <FullPageSpinner />
      )}
    </>
  );
};
